/**
 * DeviceConfig - Centralized device detection and optimization
 * Detects device capabilities and applies appropriate CSS classes
 * Based on BurrowTV-LITE-1local template (line ~7674)
 */

class DeviceConfigClass {
  constructor() {
    this.initialized = false;
    this.device = 'desktop';
    this.browser = 'unknown';
    this.isTouch = false;
    this.lowEndDevice = false;
    this.imageTier = 'std';
    this.supportsBackdropFilter = true;
    this.cpuCores = 4;
    this.deviceMemory = 4;
    this.isWebView = false;
    this.connectionType = '4g';
    this.saveData = false;
    this.effectiveType = '4g';
    
    this.init();
  }

  init() {
    if (this.initialized) return;
    
    // Detect device type
    this.device = this.detectDevice();
    
    // Detect browser
    this.browser = this.detectBrowser();
    
    // Detect touch capability
    this.isTouch = 'ontouchstart' in window || 
                   navigator.maxTouchPoints > 0 || 
                   navigator.msMaxTouchPoints > 0;
    
    // Detect WebView browsers (Telegram, Facebook, Instagram in-app)
    this.isWebView = this.detectWebView();
    
    // Detect hardware capabilities
    this.cpuCores = navigator.hardwareConcurrency || 4;
    this.deviceMemory = navigator.deviceMemory || 4; // In GB
    
    // Detect network connection
    this.detectConnection();
    
    // Determine if low-end device
    this.lowEndDevice = this.isLowEndDevice();
    
    // Set image quality tier (considers connection speed now)
    this.imageTier = this.determineImageTier();
    
    // Check backdrop-filter support
    this.supportsBackdropFilter = this.checkBackdropFilter();
    
    // Apply CSS classes to html element
    this.applyCSSClasses();
    
    this.initialized = true;
    
    console.log('DeviceConfig initialized:', {
      device: this.device,
      browser: this.browser,
      isTouch: this.isTouch,
      lowEndDevice: this.lowEndDevice,
      imageTier: this.imageTier,
      cpuCores: this.cpuCores,
      deviceMemory: this.deviceMemory,
      isWebView: this.isWebView,
      connectionType: this.connectionType,
      saveData: this.saveData
    });
  }

  detectConnection() {
    // Network Information API
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (conn) {
      this.connectionType = conn.type || 'unknown';
      this.effectiveType = conn.effectiveType || '4g'; // '4g', '3g', '2g', 'slow-2g'
      this.saveData = conn.saveData || false; // true if user enabled data saver mode
      
      // Listen for connection changes (e.g., WiFi → cellular)
      conn.addEventListener('change', () => {
        console.log('Connection changed');
        this.connectionType = conn.type || 'unknown';
        this.effectiveType = conn.effectiveType || '4g';
        this.saveData = conn.saveData || false;
        
        // Recalculate image tier based on new connection
        const oldTier = this.imageTier;
        this.imageTier = this.determineImageTier();
        
        if (oldTier !== this.imageTier) {
          console.log(`Image tier changed: ${oldTier} → ${this.imageTier} (connection: ${this.effectiveType})`);
          // Dispatch custom event so components can react
          window.dispatchEvent(new CustomEvent('imagetierchange', { 
            detail: { oldTier, newTier: this.imageTier, connection: this.effectiveType } 
          }));
        }
      });
      
      console.log('Network connection detected:', {
        type: this.connectionType,
        effectiveType: this.effectiveType,
        saveData: this.saveData
      });
    } else {
      console.log('Network Information API not supported');
    }
  }

  detectDevice() {
    const ua = navigator.userAgent.toLowerCase();
    
    if (/ipad/.test(ua)) return 'tablet';
    if (/iphone|ipod/.test(ua)) return 'mobile';
    if (/android/.test(ua)) {
      // Detect Android tablets vs phones
      return /tablet|tab/.test(ua) ? 'tablet' : 'mobile';
    }
    if (/mobile/.test(ua)) return 'mobile';
    
    // Check window width as fallback
    if (window.innerWidth <= 768) return 'mobile';
    if (window.innerWidth <= 1024) return 'tablet';
    
    return 'desktop';
  }

  detectBrowser() {
    const ua = navigator.userAgent.toLowerCase();
    
    if (ua.includes('firefox')) return 'firefox';
    if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
    if (ua.includes('chrome')) return 'chrome';
    if (ua.includes('edge')) return 'edge';
    if (ua.includes('opera') || ua.includes('opr')) return 'opera';
    
    return 'unknown';
  }

  detectWebView() {
    const ua = navigator.userAgent.toLowerCase();
    
    // Telegram WebView
    if (ua.includes('telegram')) return true;
    
    // Facebook/Instagram in-app browser
    if (ua.includes('fban') || ua.includes('fbav') || ua.includes('instagram')) return true;
    
    // Other common WebViews
    if (ua.includes('webview') || ua.includes('wv')) return true;
    
    return false;
  }

  isLowEndDevice() {
    // CPU cores <= 2 OR RAM <= 2GB = low-end (relaxed from 4 — was too aggressive)
    if (this.cpuCores <= 2) return true;
    if (this.deviceMemory <= 2) return true;
    
    // Only flag mobile as low-end if also low on resources
    if (this.device === 'mobile' && (this.cpuCores <= 4 || this.deviceMemory <= 4)) return true;
    
    return false;
  }

  determineImageTier() {
    // Downgrade for slow connections or data saver mode
    if (this.effectiveType === '2g' || this.effectiveType === 'slow-2g' || this.saveData) {
      console.log(`Image tier downgraded to 'low' due to connection: ${this.effectiveType}, saveData: ${this.saveData}`);
      return 'low';
    }
    
    // 3G connections get low tier on mobile, std on desktop
    if (this.effectiveType === '3g') {
      return this.device === 'mobile' ? 'low' : 'std';
    }
    
    // Low-end devices and mobile get 'low' tier
    if (this.lowEndDevice || this.device === 'mobile') {
      return 'low';
    }
    
    // Tablets get standard
    if (this.device === 'tablet') {
      return 'std';
    }
    
    // Desktop gets standard or high based on screen resolution
    if (window.innerWidth >= 1920 && window.innerHeight >= 1080) {
      return 'high';
    }
    
    return 'std';
  }

  checkBackdropFilter() {
    if (typeof CSS === 'undefined' || !CSS.supports) return false;
    
    return CSS.supports('backdrop-filter', 'blur(10px)') || 
           CSS.supports('-webkit-backdrop-filter', 'blur(10px)');
  }

  applyCSSClasses() {
    const html = document.documentElement;
    
    // Device type
    html.classList.add(`device-${this.device}`);
    
    // Browser
    html.classList.add(`browser-${this.browser}`);
    
    // Touch
    if (this.isTouch) {
      html.classList.add('touch-enabled');
    }
    
    // Low-end device
    if (this.lowEndDevice) {
      html.classList.add('low-end-device');
    }
    
    // Mobile optimization
    if (this.device === 'mobile' || this.lowEndDevice) {
      html.classList.add('mobile-optimized');
    }
    
    // Backdrop filter support
    if (!this.supportsBackdropFilter) {
      html.classList.add('no-backdrop-filter');
    }
    
    // WebView
    if (this.isWebView) {
      html.classList.add('webview-browser');
    }
  }

  // Update on window resize (debounced)
  handleResize = this.debounce(() => {
    const newDevice = this.detectDevice();
    if (newDevice !== this.device) {
      const html = document.documentElement;
      html.classList.remove(`device-${this.device}`);
      this.device = newDevice;
      html.classList.add(`device-${this.device}`);
      
      // Recalculate image tier
      this.imageTier = this.determineImageTier();
    }
  }, 250);

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Public getters
  isMobile() {
    return this.device === 'mobile';
  }

  isTablet() {
    return this.device === 'tablet';
  }

  isDesktop() {
    return this.device === 'desktop';
  }

  isTouchDevice() {
    return this.isTouch;
  }

  isLowEnd() {
    return this.lowEndDevice;
  }

  getImageTier() {
    return this.imageTier;
  }

  getBrowser() {
    return this.browser;
  }

  isInWebView() {
    return this.isWebView;
  }

  getConnectionType() {
    return this.connectionType;
  }

  getEffectiveType() {
    return this.effectiveType;
  }

  isSlowConnection() {
    return this.effectiveType === '2g' || 
           this.effectiveType === 'slow-2g' || 
           this.effectiveType === '3g';
  }

  isSaveDataEnabled() {
    return this.saveData;
  }
}

// Singleton instance
const DeviceConfig = new DeviceConfigClass();

// Add resize listener
if (typeof window !== 'undefined') {
  window.addEventListener('resize', DeviceConfig.handleResize);
}

export default DeviceConfig;
